import { useState, type ChangeEvent } from "react";
import { PORTABLE_TOTAL_BLOB_BYTES_MAX, type PortableDataset } from "@tchoma/storage";
import { Button, Notice, Sheet, SheetActions } from "@tchoma/ui-riri";
import {
  DELETE_ALL_CONFIRMATION,
  openInboxRepository,
  type InboxImportPolicy,
  type InboxImportPreview,
} from "./repository";

// Base64 adds roughly one third; leave room for document metadata without
// attempting to parse an unbounded file into browser memory.
const PORTABLE_JSON_BYTES_MAX = Math.ceil(PORTABLE_TOTAL_BLOB_BYTES_MAX * 4 / 3) + 16 * 1024 * 1024;

export function DataOwnershipPanel() {
  const [pendingDataset, setPendingDataset] = useState<unknown>(null);
  const [preview, setPreview] = useState<InboxImportPreview | null>(null);
  const [replaceArmed, setReplaceArmed] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [recoveryDataset, setRecoveryDataset] = useState<PortableDataset | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ tone: "success" | "error" | "info"; title: string; body: string } | null>(null);

  const exportData = async () => {
    setBusy("export");
    try {
      const repository = await openInboxRepository();
      downloadDataset(await repository.exportDataset(), "backup");
      setNotice({ tone: "success", title: "Export prepared", body: "The portable JSON includes live entries, tombstones and attachment bytes." });
    } catch {
      setNotice({ tone: "error", title: "Export unavailable", body: "Inbox could not prepare a local export on this device." });
    } finally {
      setBusy(null);
    }
  };

  const inspectImport = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = "";
    if (!file) return;
    setBusy("preview");
    setPendingDataset(null);
    setPreview(null);
    setReplaceArmed(false);
    try {
      if (file.size > PORTABLE_JSON_BYTES_MAX) throw new Error("too-large");
      const candidate: unknown = JSON.parse(await file.text());
      const repository = await openInboxRepository();
      const nextPreview = await repository.previewImport(candidate);
      setPendingDataset(candidate);
      setPreview(nextPreview);
      setNotice({ tone: "info", title: "Review before importing", body: "Nothing has changed yet. Choose Merge or explicitly approve Replace." });
    } catch {
      setNotice({ tone: "error", title: "Import not accepted", body: "Choose an intact local-only portable Inbox export. Existing data has not changed." });
    } finally {
      setBusy(null);
    }
  };

  const importData = async (policy: InboxImportPolicy) => {
    if (!preview || pendingDataset === null || (policy === "replace" && !replaceArmed)) return;
    setBusy(policy);
    try {
      const repository = await openInboxRepository();
      const result = await repository.importDataset(pendingDataset, policy);
      setPendingDataset(null);
      setPreview(null);
      setReplaceArmed(false);
      setNotice({
        tone: "success",
        title: policy === "replace" ? "Inbox replaced from backup" : "New backup items merged",
        body: policy === "merge"
          ? `${result.imported.documents} portable records processed; ${result.skippedCollisions} existing records were kept.`
          : `${result.imported.documents} portable records and ${result.imported.blobs} blobs restored.`,
      });
    } catch {
      setNotice({ tone: "error", title: "Import failed safely", body: "The backup was not applied. Keep the source file and try again." });
    } finally {
      setBusy(null);
    }
  };

  const deleteEverything = async () => {
    if (deleteConfirmation !== DELETE_ALL_CONFIRMATION) return;
    setBusy("delete");
    try {
      const repository = await openInboxRepository();
      const preparedRecovery = await repository.exportDataset();
      setRecoveryDataset(preparedRecovery);
      downloadDataset(preparedRecovery, "recovery");
      const result = await repository.deleteAll(deleteConfirmation);
      if (!result.cleared) throw new Error("not-cleared");
      setRecoveryDataset(result.recoveryDataset);
      setDeleteOpen(false);
      setDeleteConfirmation("");
      setPendingDataset(null);
      setPreview(null);
      setNotice({
        tone: "success",
        title: "Local Inbox deleted",
        body: `${result.removed.documents} portable records and ${result.removed.blobs} blobs were removed. A recovery export is available below.`,
      });
    } catch {
      setNotice({ tone: "error", title: "Delete did not complete", body: "Inbox could not verify a complete local deletion. No success is being claimed; use the prepared recovery export below." });
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="inbox-settings-section" aria-labelledby="inbox-data-title">
      <div className="inbox-section-heading">
        <div>
          <h2 id="inbox-data-title">Data ownership</h2>
          <p>Portable JSON includes entries, deleted-record tombstones and attachment bytes.</p>
        </div>
        <Button tone="secondary" disabled={busy !== null} onClick={() => void exportData()}>Export everything</Button>
      </div>

      <div className="inbox-ownership-actions">
        <label className="inbox-file-field inbox-import-file">
          <span>Preview a portable export</span>
          <input type="file" accept="application/json,.json" disabled={busy !== null} onChange={(event) => void inspectImport(event)} />
        </label>
        <Button tone="quiet" disabled={busy !== null} onClick={() => setDeleteOpen(true)}>Delete all local data</Button>
      </div>

      {preview ? (
        <div className="inbox-import-preview" aria-label="Import preview">
          <h3>Import preview</h3>
          <dl className="inbox-summary-grid">
            <Summary label="Live entries" value={preview.liveEntries} />
            <Summary label="Tombstones" value={preview.tombstones} />
            <Summary label="Attachments" value={preview.blobs} />
            <Summary label="Existing ID matches" value={preview.collisions} />
          </dl>
          <p>Merge keeps every existing record when an ID matches. Replace removes the current dataset first.</p>
          <div className="inbox-import-actions">
            <Button disabled={busy !== null} onClick={() => void importData("merge")}>Merge new records</Button>
            <label className="inbox-confirm-check">
              <input type="checkbox" checked={replaceArmed} onChange={(event) => setReplaceArmed(event.currentTarget.checked)} />
              <span>I understand Replace removes the current Inbox.</span>
            </label>
            <Button tone="danger" disabled={busy !== null || !replaceArmed} onClick={() => void importData("replace")}>Replace from this export</Button>
          </div>
        </div>
      ) : null}

      {recoveryDataset ? (
        <Button tone="secondary" onClick={() => downloadDataset(recoveryDataset, "recovery")}>Download recovery export again</Button>
      ) : null}
      {notice ? <Notice tone={notice.tone} title={notice.title}>{notice.body}</Notice> : null}

      <Sheet
        open={deleteOpen}
        title="Delete every local Inbox record?"
        description="This clears live entries, tombstones, outbox records and attachment blobs. A recovery export is prepared in memory and offered as a download."
        onOpenChange={(open) => {
          if (busy === "delete") return;
          setDeleteOpen(open);
          if (!open) setDeleteConfirmation("");
        }}
      >
        <label className="inbox-delete-confirmation">
          <span>Type <strong>{DELETE_ALL_CONFIRMATION}</strong> to continue.</span>
          <input
            value={deleteConfirmation}
            autoComplete="off"
            spellCheck={false}
            onChange={(event) => setDeleteConfirmation(event.currentTarget.value)}
          />
        </label>
        <SheetActions>
          <Button tone="danger" disabled={busy !== null || deleteConfirmation !== DELETE_ALL_CONFIRMATION} onClick={() => void deleteEverything()}>
            {busy === "delete" ? "Deleting…" : "Export recovery copy and delete all"}
          </Button>
        </SheetActions>
      </Sheet>
    </section>
  );
}

function Summary({ label, value }: { label: string; value: number }) {
  return <div><dt>{label}</dt><dd>{value}</dd></div>;
}

function downloadDataset(dataset: PortableDataset, suffix: "backup" | "recovery"): void {
  const objectUrl = URL.createObjectURL(new Blob([JSON.stringify(dataset, null, 2)], { type: "application/json" }));
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = `tchoma-inbox-${suffix}-${new Date().toISOString().slice(0, 10)}.json`;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}
